/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { DeploymentFreezeScopeDetail, DeploymentFreezeDetailEnvironment, EnvironmentResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useState } from "react";
import { repository } from "~/clientInstance";
import { EnvironmentChip } from "~/components/Chips";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import type { DeploymentFreezeModel } from "~/components/DeploymentFreezes/CreateOrEditDeploymentFreeze";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { ExpandableFormSection, Summary } from "~/components/form";
interface ProjectFreezeEnvironmentSectionProps {
    deploymentFreeze: DeploymentFreezeModel;
    updateFreezeModel: (deploymentFreeze: DeploymentFreezeModel) => void;
    onEnvironmentUpdate: (item: DeploymentFreezeScopeDetail, selectedEnvironments: DeploymentFreezeDetailEnvironment[]) => void;
    doBusyTask: DoBusyTask;
    project?: ProjectResource;
}
function createEmptyProjectScope(project?: ProjectResource): DeploymentFreezeScopeDetail {
    return {
        Id: project?.Id ?? "",
        Name: project?.Name ?? "",
        Environments: [],
        LogoLink: "",
        SpaceId: project?.SpaceId ?? "",
    };
}
export const ProjectFreezeEnvironmentSection: React.FC<ProjectFreezeEnvironmentSectionProps> = ({ deploymentFreeze, onEnvironmentUpdate, doBusyTask, project, updateFreezeModel }) => {
    const [environments, setEnvironments] = useState<EnvironmentResource[]>([]);
    const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);
    const [projectScope, setProjectScope] = useState<DeploymentFreezeScopeDetail>();
    const handleEnvironmentChange = (environmentIds: string[]) => {
        setSelectedEnvironments(environmentIds);
        if (projectScope) {
            onEnvironmentUpdate(projectScope, environments.filter((e) => environmentIds.includes(e.Id)));
        }
    };
    useEffect(() => {
        const loadEnvironments = async () => {
            if (project?.Id && selectedEnvironments.length === 0) {
                const response = await repository.Projects.getAvailableEnvironmentsForProject(project.Id);
                setEnvironments(response.Environments);
            }
        };
        loadEnvironments();
        let projectScope = deploymentFreeze.projectScope.find((s) => s.Id === project?.Id);
        if (!projectScope) {
            projectScope = createEmptyProjectScope(project);
            deploymentFreeze.projectScope.push(projectScope);
            updateFreezeModel(deploymentFreeze);
        }
        setProjectScope(projectScope);
        setSelectedEnvironments(projectScope.Environments.map((e) => e.Id));
    }, [project, deploymentFreeze, updateFreezeModel, selectedEnvironments.length]);
    const buildEnvironmentSummary = () => {
        const staticEnvironmentChips = selectedEnvironments.map((envId) => {
            const environment = environments.find((e) => e.Id === envId);
            return <EnvironmentChip environmentName={environment?.Name || envId} key={envId}/>;
        });
        if (staticEnvironmentChips.length > 0) {
            return Summary.summary(<div>{staticEnvironmentChips}</div>);
        }
        return Summary.placeholder("No environments are selected");
    };
    return (<ExpandableFormSection key="environment" errorKey="environment" title="Environments" summary={buildEnvironmentSummary()} help="Select the environments to be frozen for this project." containerKey="FreezeEdit" isExpandedByDefault={true}>
            <EnvironmentMultiSelect accessibleName="Environments that to be frozen for this project" value={selectedEnvironments} items={environments} onChange={handleEnvironmentChange}/>
        </ExpandableFormSection>);
};
ProjectFreezeEnvironmentSection.displayName = "ProjectFreezeEnvironmentSection"
